import { z } from 'zod';

const INVALID_FILENAME_CHARS_PATTERN = /[<>:"/\\|?*]+/g;
const INVALID_FILENAME_CHARS_VALIDATION_PATTERN = /[<>:"/\\|?*]/;
const MULTI_WHITESPACE_PATTERN = /\s+/g;
const SEPARATOR_COLLAPSE_PATTERN = /-+/g;
const WINDOWS_RESERVED_NAMES_PATTERN = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])$/i;

const CONTROL_CHAR_MAX_CODE = 31;
const DOT_CHAR = '.';
const SPACE_CHAR = ' ';
const HYPHEN_CHAR = '-';

const normalizeWhitespace = (value: string): string =>
  value.trim().replace(MULTI_WHITESPACE_PATTERN, ' ');

const isControlCharacter = (char: string): boolean => char.charCodeAt(0) <= CONTROL_CHAR_MAX_CODE;

const containsControlCharacters = (value: string): boolean => {
  for (const char of value) {
    if (isControlCharacter(char)) return true;
  }

  return false;
};

const replaceControlCharacters = (value: string): string => {
  let sanitized = '';
  for (const char of value) {
    sanitized += isControlCharacter(char) ? HYPHEN_CHAR : char;
  }
  return sanitized;
};

const isEdgeSeparator = (char: string | undefined): boolean =>
  char === HYPHEN_CHAR || char === DOT_CHAR || char === SPACE_CHAR;

const trimEdgeSeparators = (value: string): string => {
  let start = 0;
  let end = value.length;

  while (start < end && isEdgeSeparator(value[start])) start += 1;
  while (end > start && isEdgeSeparator(value[end - 1])) end -= 1;

  return value.slice(start, end);
};

const trimTrailingDotsAndSpaces = (value: string): string => {
  let end = value.length;
  while (end > 0) {
    const current = value[end - 1];
    if (current !== DOT_CHAR && current !== SPACE_CHAR) break;
    end -= 1;
  }
  return value.slice(0, end);
};

const hasTrailingDotOrSpace = (value: string): boolean => {
  if (!value) return false;
  const trailingCharacter = value[value.length - 1];
  return trailingCharacter === DOT_CHAR || trailingCharacter === SPACE_CHAR;
};

export const exportFilenameSchema = z.string().superRefine((value, ctx) => {
  const normalized = normalizeWhitespace(value);
  if (!normalized) {
    ctx.addIssue({
      code: 'custom',
      message: 'Export name cannot be empty'
    });
    return;
  }

  if (
    containsControlCharacters(normalized) ||
    INVALID_FILENAME_CHARS_VALIDATION_PATTERN.test(normalized)
  ) {
    ctx.addIssue({
      code: 'custom',
      message: 'Export name contains unsupported filename characters'
    });
    return;
  }

  if (hasTrailingDotOrSpace(normalized)) {
    ctx.addIssue({
      code: 'custom',
      message: 'Export name cannot end with a dot or space'
    });
    return;
  }

  if (WINDOWS_RESERVED_NAMES_PATTERN.test(normalized)) {
    ctx.addIssue({
      code: 'custom',
      message: 'Export name is reserved by the operating system'
    });
  }
});

export const getFilenameSanitizationError = (value: string): string | null => {
  const result = exportFilenameSchema.safeParse(value);
  if (result.success) return null;
  return result.error.issues[0]?.message ?? 'Export name contains unsupported filename characters';
};

export const buildDisplayDateSuffix = (formattedDate: string, fallbackIsoDate: string): string => {
  const normalizedFormattedDate = normalizeWhitespace(formattedDate);
  if (normalizedFormattedDate) return normalizedFormattedDate;

  return fallbackIsoDate.slice(0, 10);
};

export const sanitizeFilenamePart = (value: string): string => {
  const normalized = normalizeWhitespace(value);
  const withoutInvalidChars = trimTrailingDotsAndSpaces(
    trimEdgeSeparators(
      replaceControlCharacters(normalized)
        .replace(INVALID_FILENAME_CHARS_PATTERN, '-')
        .replace(SEPARATOR_COLLAPSE_PATTERN, '-')
    )
  );

  if (!withoutInvalidChars) return '';
  if (WINDOWS_RESERVED_NAMES_PATTERN.test(withoutInvalidChars)) {
    return `${withoutInvalidChars}-file`;
  }

  return withoutInvalidChars;
};

export const buildExportFileDateSuffix = (
  formattedDate: string,
  fallbackIsoDate: string
): string => {
  const displaySuffix = buildDisplayDateSuffix(formattedDate, fallbackIsoDate);
  const sanitizedSuffix = sanitizeFilenamePart(displaySuffix);
  return sanitizedSuffix || 'date';
};
